import React, { useState, useEffect, useRef } from 'react';
import './StudentRegistration.css';
import collegeConfig from '../../config/collegeConfig';
import { MdEmail, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { RiLockPasswordFill } from 'react-icons/ri';
import { FaUser, FaPhone, FaBook, FaArrowLeft, FaGraduationCap } from 'react-icons/fa';
import AppDownloadBanner from '../AppDownloadBanner/AppDownloadBanner';

const loadRazorpay = () =>
  new Promise(resolve => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const StudentRegistration = ({ onBack, onSuccess }) => {
  const [settings, setSettings] = useState(null);
  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    level: '',
    courseId: '',
    batch: ''
  });

  const batchOptions = (() => {
    const currentYear = new Date().getFullYear();
    const options = [];
    for (let startYear = currentYear - 4; startYear <= currentYear; startYear++) {
      options.push(`${startYear}-${startYear + 3}`);
    }
    return options;
  })();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const errorRef = useRef(null);
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [successEmail, setSuccessEmail] = useState('');
  useEffect(() => {
    Promise.all([fetchSettings(), fetchCourses()]);
  }, []);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/payment/settings`);
      const data = await res.json();
      if (data.success) setSettings(data.settings);
    } catch (e) {
      setError('Unable to load payment settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/payment/courses`);
      const data = await res.json();
      if (data.success) setCourses(data.courses);
    } catch (e) {
      console.error('Failed to fetch courses', e);
    }
  };

  const handleChange = e => {
    setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
    if (error) setError('');
  };

  const filteredCourses = formData.level
    ? courses.filter(c => c.level === formData.level)
    : [];

  const validate = () => {
    if (!formData.firstName.trim()) return 'First name is required';
    if (!formData.lastName.trim()) return 'Last name is required';
    if (!formData.email.trim()) return 'Email is required';
    if (!/\S+@\S+\.\S+/.test(formData.email)) return 'Enter a valid email address';
    if (!formData.phone.trim()) return 'Phone number is required';
    if (!formData.password) return 'Password is required';
    if (formData.password.length < 6) return 'Password must be at least 6 characters';
    if (formData.password !== formData.confirmPassword) return 'Passwords do not match';
    if (!formData.level) return 'Please select your programme type';
    if (!formData.courseId) return 'Please select a course';
    if (!formData.batch) return 'Please select your batch year';
    return null;
  };

  const handlePay = async e => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const loaded = await loadRazorpay();
      if (!loaded) {
        setError('Failed to load payment gateway. Please check your internet connection.');
        setProcessing(false);
        return;
      }

      // Create order — send the full form now (not just email) so the
      // account can still be created via webhook if this browser tab never
      // gets to call verify-and-register after payment.
      const orderRes = await fetch(`${collegeConfig.api.baseURL}/payment/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          phone: formData.phone,
          courseId: formData.courseId,
          batch: formData.batch
        })
      });
      const orderData = await orderRes.json();
      if (!orderData.success) {
        if (orderData.message && orderData.message.includes('already registered')) {
          setError('This email is already registered. Please login to your account. If your subscription has expired, you can renew it after logging in.');
        } else {
          setError(orderData.message || 'Failed to create payment order');
        }
        setProcessing(false);
        return;
      }

      const options = {
        key: orderData.key,
        amount: orderData.order.amount,
        currency: 'INR',
        name: collegeConfig.collegeName,
        description: `E-Logbook Access — ${settings?.paymentType === 'annual_recurring' ? '1 Year' : `${settings?.accountValidityYears} Year(s)`}`,
        image: collegeConfig.logo,
        order_id: orderData.order.id,
        prefill: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          contact: formData.phone
        },
        theme: { color: collegeConfig.theme?.primaryColor || '#b11217' },
        handler: async paymentResponse => {
          try {
            const regRes = await fetch(`${collegeConfig.api.baseURL}/payment/verify-and-register`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature
              })
            });
            const regData = await regRes.json();
            if (regData.success) {
              setSuccessEmail(regData.email);
              setStep('success');
              if (onSuccess) onSuccess(regData.email);
            } else {
              setError(regData.message || 'Account creation failed after payment. Please contact support.');
            }
          } catch {
            setError('Account creation failed after payment. Please contact support with your payment ID: ' + paymentResponse.razorpay_payment_id);
          } finally {
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: () => setProcessing(false)
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error('Payment error:', err);
      setError('Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="sr-container">
        <div className="sr-card">
          <div className="sr-loading">
            <div className="sr-spinner"></div>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!settings || settings.paymentMode !== 'student') {
    return (
      <div className="sr-container">
        <div className="sr-card">
          <div className="sr-unavailable">
            <div className="sr-unavailable-icon">🔒</div>
            <h3>Self-registration is not enabled</h3>
            <p>Please contact the institute for account creation.</p>
            <button className="sr-back-btn" onClick={onBack}>
              <FaArrowLeft /> Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="sr-container">
        <div className="sr-card">
          <div className="sr-success">
            <div className="sr-success-icon">✅</div>
            <h2>Registration Successful!</h2>
            <p>Your e-logbook account has been created.</p>
            <div className="sr-success-detail">
              <strong>Login Email:</strong> {successEmail}
            </div>
            <p className="sr-success-sub">
              {settings.paymentType === 'annual_recurring'
                ? 'Your account is active for 1 year.'
                : `Your account is active for ${settings.accountValidityYears} year(s).`}
            </p>

            <AppDownloadBanner />

            <button className="sr-login-btn" onClick={onBack} style={{ marginTop: 16 }}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sr-container">
      <div className="sr-background">
        <div className="sr-bg-element sr-el-1"></div>
        <div className="sr-bg-element sr-el-2"></div>
      </div>

      <div className="sr-card">
        <div className="sr-header">
          <img src={collegeConfig.logo} alt="Logo" className="sr-logo" />
          <h1 className="sr-college">{collegeConfig.collegeName}</h1>
          <h2 className="sr-title">Purchase E-Logbook Access</h2>
          <div className="sr-price-badge">
            
            {settings.paymentType === 'annual_recurring'
              ? 'Annual Subscription'
              : `${settings.accountValidityYears} Year${settings.accountValidityYears > 1 ? 's' : ''} Access`}
          </div>
        </div>

        {error && (
          <div ref={errorRef} className="sr-error">
            {error}
            {error.includes('already registered') && (
              <button type="button" onClick={onBack} style={{ display: 'block', marginTop: 10, padding: '8px 20px', background: '#b11217', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                Go to Login
              </button>
            )}
          </div>
        )}

        <form onSubmit={handlePay} className="sr-form">
          <div className="sr-row">
            <div className="sr-field">
              <div className="sr-input-wrap">
                <span className="sr-icon"><FaUser size={16} /></span>
                <input
                  type="text"
                  name="firstName"
                  className="sr-input"
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="sr-field">
              <div className="sr-input-wrap">
                <span className="sr-icon"><FaUser size={16} /></span>
                <input
                  type="text"
                  name="lastName"
                  className="sr-input"
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="sr-field">
            <div className="sr-input-wrap">
              <span className="sr-icon"><MdEmail size={18} /></span>
              <input
                type="email"
                name="email"
                className="sr-input"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="sr-field">
            <div className="sr-input-wrap">
              <span className="sr-icon"><FaPhone size={15} /></span>
              <input
                type="tel"
                name="phone"
                className="sr-input"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="sr-field">
            <div className="sr-level-label">Programme Type</div>
            <div className="sr-level-selector">
              {['UG', 'PG', 'Super Speciality'].map(lvl => (
                <button
                  key={lvl}
                  type="button"
                  className={`sr-level-btn ${formData.level === lvl ? 'active' : ''}`}
                  onClick={() => {
                    setFormData(p => ({ ...p, level: lvl, courseId: '', batch: '' }));
                    if (error) setError('');
                  }}
                >
                  {lvl}
                </button>
              ))}
            </div>
          </div>

          {formData.level && (
            <div className="sr-field">
              <div className="sr-input-wrap">
                <span className="sr-icon"><FaBook size={16} /></span>
                <select
                  name="courseId"
                  className="sr-input sr-select"
                  value={formData.courseId}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Course</option>
                  {filteredCourses.map(c => (
                    <option key={c._id} value={c._id}>{c.name} {c.code ? `(${c.code})` : ''}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {formData.courseId && (
            <div className="sr-field">
              <div className="sr-input-wrap">
                <span className="sr-icon"><FaGraduationCap size={16} /></span>
                <select
                  name="batch"
                  className="sr-input sr-select"
                  value={formData.batch}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Batch Year</option>
                  {batchOptions.map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="sr-field">
            <div className="sr-input-wrap">
              <span className="sr-icon"><RiLockPasswordFill size={18} /></span>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                className="sr-input"
                placeholder="Create Password"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                className="sr-eye"
                onClick={() => setShowPassword(p => !p)}
              >
                {showPassword ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />}
              </button>
            </div>
            {formData.password.length > 0 && formData.password.length < 6 && (
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#c62828' }}>
                Password must be at least 6 characters
              </p>
            )}
          </div>

          <div className="sr-field">
            <div className="sr-input-wrap">
              <span className="sr-icon"><RiLockPasswordFill size={18} /></span>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                className="sr-input"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                className="sr-eye"
                onClick={() => setShowConfirmPassword(p => !p)}
              >
                {showConfirmPassword ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />}
              </button>
            </div>
            {formData.confirmPassword.length > 0 && formData.password !== formData.confirmPassword && (
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#c62828' }}>
                Passwords do not match
              </p>
            )}
          </div>

          <button
            type="submit"
            className={`sr-pay-btn ${processing ? 'loading' : ''}`}
            disabled={processing}
          >
            {processing ? (
              <>
                <span className="sr-spinner"></span> Processing...
              </>
            ) : (
              `Register & Pay`
            )}
          </button>
        </form>

        <div className="sr-footer">
          <button className="sr-back-link" type="button" onClick={onBack}>
            <FaArrowLeft size={12} style={{ marginRight: 6 }} />
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentRegistration;
